<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div
    class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col pb-20">
    <!-- Toast Notification -->
    <Toast :show="showToast" :message="toastMessage" :type="toastType" />

    <!-- Eartag Prefix Info Modal -->
    <EartagPrefixInfoModal
      v-model:show="showPrefixInfoModal"
      @setup-prefix="handleSetupPrefix"
      @continue="handleContinueWithoutPrefix" />

    <div v-if="!loading && goats.length > 0" class="px-4 mb-4">
      <div class="grid grid-cols-3 gap-3">
        <div
          class="bg-[var(--color-primary-100)] p-3 rounded-lg text-center border border-[var(--color-primary-400)]">
          <div class="text-xl font-bold">{{ goatCount }}</div>
          <div class="text-[10px] uppercase">{{ $t('views.goats.total') }}</div>
        </div>
        <div
          class="bg-[var(--color-primary-100)] p-3 rounded-lg text-center border border-[var(--color-primary-400)]">
          <div class="text-xl font-bold">{{ maleGoats.length }}</div>
          <div class="text-[10px] uppercase">{{ $t('views.goats.males') }}</div>
        </div>
        <div
          class="bg-[var(--color-primary-100)] p-3 rounded-lg text-center border border-[var(--color-primary-400)]">
          <div class="text-xl font-bold">{{ femaleGoats.length }}</div>
          <div class="text-[10px] uppercase">
            {{ $t('views.goats.females') }}
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="error"
      class="mx-4 mb-4 p-4 bg-red-100 text-red-800 rounded-lg shadow">
      {{ error }}
      <button @click="clearError" class="ml-2 underline">OK</button>
    </div>

    <div class="flex-1 px-4 flex flex-col overflow-hidden">
      <ListTableModule
        v-if="!loading && goats.length > 0"
        :data="enrichedGoats"
        :columns="tableColumns"
        :filters="tableFilters"
        :loading="loading"
        @row-click="handleRowClick"
        @refresh="refreshGoats">
        <template #cell-eartag="{ value }">
          <span :title="value" class="font-mono">
            ..{{ value ? String(value).slice(-5) : '-' }}
          </span>
        </template>

        <template #cell-gender="{ value }">
          <span
            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold gap-1"
            :class="
              value === 'male'
                ? 'bg-[var(--color-primary-200)] text-[var(--color-primary-900)]'
                : 'bg-[var(--color-primary-200)] text-[var(--color-primary-900)]'
            ">
            <Mars v-if="value === 'male'" class="w-3 h-3 fill-current" />
            <Venus v-else class="w-3 h-3 fill-current" />
          </span>
        </template>
      </ListTableModule>

      <!-- Empty State -->
      <div
        v-else-if="!loading && goats.length === 0"
        class="flex-1 flex items-center justify-center px-4">
        <div class="text-center">
          <div class="text-6xl mb-4"></div>

          <h2 class="text-xl font-bold mb-2">
            {{ $t('views.goats.noGoatsYet') }}
          </h2>

          <p class="text-[var(--color-text)] mb-4">
            {{ $t('views.goats.startCreating') }}
          </p>

          <button
            @click="handleAddGoat"
            class="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[var(--color-primary-200)] text-[var(--color-primary-900)] rounded-lg border border-[var(--color-primary-400)] hover:bg-[var(--color-primary-300)] transition-colors duration-200 font-semibold">
            <PlusCircle class="w-5 h-5" />
            {{ $t('views.goats.newGoat') }}
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-else class="flex-1 flex items-center justify-center px-4">
        <span>{{ $t('common.loading') }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, onMounted, ref, watch, onUnmounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { useGoatStore } from '@/stores/goat.store.js'
  import { useGroupStore } from '@/stores/group.store.js'
  import { useUiStore } from '@/stores/ui.store.js'
  import { storeToRefs } from 'pinia'
  import { PlusCircle, Mars, Venus, ShieldIcon } from 'lucide-vue-next'
  import ActionButton from '@/components/ui/buttons/ActionButton.vue'
  import ListTableModule from '@/components/ui/tables/ListTable.vue'
  import Toast from '@/components/ui/feedback/Toast.vue'
  import EartagPrefixInfoModal from '@/components/ui/feedback/EartagPrefixInfoModal.vue'

  import { useI18n } from 'vue-i18n'

  const router = useRouter()
  const goatStore = useGoatStore()
  const groupStore = useGroupStore()
  const uiStore = useUiStore()

  const { goats, loading, error, goatCount, maleGoats, femaleGoats } =
    storeToRefs(goatStore)
  const { groups } = storeToRefs(groupStore)
  const { fetchGoats, clearError } = goatStore
  const { fetchGroups } = groupStore
  const { consumeToast } = uiStore

  const { t } = useI18n()

  // Toast notification state
  const showToast = ref(false)
  const showPrefixInfoModal = ref(false)

  /**
   * Handle add goat button - show info modal if appropriate, otherwise navigate
   */
  function handleAddGoat() {
    if (uiStore.shouldShowPrefixInfo()) {
      showPrefixInfoModal.value = true
      uiStore.markPrefixInfoShown()
    } else {
      router.push({ name: 'NewGoat' })
    }
  }

  //header
  watch(
    goatCount,
    () => {
      uiStore.setHeader({
        title: t('views.goats.title'),
        showAdd: true,
        addAction: handleAddGoat,
      })
    },
    { immediate: true }
  )

  const toastMessage = ref('')
  const toastType = ref('saved')

  // Prepare Data
  const enrichedGoats = computed(() => {
    return goats.value.map((goat) => {
      const group = groups.value.find((h) => h.id === goat.groupId)
      return {
        ...goat,
        groupName: group ? group.name : '-',
      }
    })
  })

  // Table Columns Configuration

  const tableColumns = [
    {
      key: 'eartag',
      label: 'views.goats.table.eartag',
      type: 'text',
      simple: true,
    },
    {
      key: 'name',
      label: 'views.goats.table.name',
      type: 'text',
      simple: true,
    },
    {
      key: 'dateOfBirth',
      label: 'views.goats.table.birthDate',
      type: 'date',
      simple: true,
    },

    // not in simple mode
    {
      key: 'gender',
      label: 'views.goats.table.gender',
      type: 'text',
    },
    {
      key: 'breed',
      label: 'views.goats.table.breed',
      type: 'text',
    },
    {
      key: 'groupName',
      label: 'views.goats.table.group',
      type: 'text',
    },
  ]

  // Dynamic Filter Configuration
  const tableFilters = computed(() => {
    // Extract unique breeds and group names dynamically
    const uniqueBreeds = [
      ...new Set(goats.value.map((g) => g.breed).filter(Boolean)),
    ].sort()
    const uniqueGroups = [
      ...new Set(groups.value.map((g) => g.name).filter(Boolean)),
    ].sort()
    //keys
    return [
      { key: 'gender', label: 'Geschlecht', options: ['Male', 'Female'] },
      { key: 'breed', label: 'Rasse', options: uniqueBreeds },
      { key: 'groupName', label: 'Gruppe', options: uniqueGroups },
    ]
  })

  /**
   * Show toast notification
   */
  function showToastNotification(message, type = 'saved') {
    showToast.value = false
    toastMessage.value = message
    toastType.value = type
    setTimeout(() => {
      showToast.value = true
    }, 10)
  }

  // Handle Actions
  const handleRowClick = (goat) => {
    console.log('Navigating to goat:', goat.id)
    router.push({ name: 'GoatDetail', params: { id: goat.id } })
  }

  const handleSetupPrefix = (dontShowAgain) => {
    if (dontShowAgain) {
      uiStore.dismissPrefixInfo()
    }
    router.push({ name: 'EartagPrefixSettings' })
  }

  const handleContinueWithoutPrefix = (dontShowAgain) => {
    if (dontShowAgain) {
      uiStore.dismissPrefixInfo()
    }
    router.push({ name: 'NewGoat' })
  }

  const refreshGoats = async () => {
    await Promise.all([fetchGroups(), fetchGoats()])
  }

  onMounted(async () => {
    if (groups.value.length === 0) await fetchGroups()
    if (goats.value.length === 0) await fetchGoats()

    // Check for pending toast notification from UI store
    const pendingToast = consumeToast()
    if (pendingToast) {
      showToastNotification(pendingToast.message, pendingToast.type)
    }
  })
</script>
